/**
 * Copyright (c) 2025-2026 Four Hour Window LLC
 * All rights reserved.
 * 
 * This source code is proprietary and confidential.
 * Unauthorized copying, distribution, or use is strictly prohibited.
 */

import React from 'react';
import { formatCurrency } from '../../utils/formatters.ts';

interface OrderItemsExpandedProps {
  order: any;
}

const OrderItemsExpanded: React.FC<OrderItemsExpandedProps> = ({ order }) => {
  return (
    <div className="expanded-section order-items-expanded">
      <h4 className="expanded-title">
        Order Items for #{order.orderNumber}
      </h4>
      <div className="order-items-container">
        {order.lineItems?.map((lineItem: any, index: number) => {
          return (
            <div key={index} className="panel line-item-panel">
              <div className="id-and-title line-item-content">
                {lineItem.imageUrl && (
                  <a className="product-image-link" href={`https://merch-sherpa.com/variants/${lineItem.variantId}`} target="_blank" rel="noreferrer">
                    <img 
                      className="product-image"
                      width="80" 
                      height="80" 
                      src={lineItem.imageUrl} 
                      alt={`${lineItem.productTitle || 'Product'} - ${lineItem.variantTitle || 'Variant'}`}
                      onError={(e) => {
                        const target = e.target as HTMLImageElement;
                        target.style.display = 'none';
                      }}
                    />
                  </a>
                )}
                <div className="identifier product-details">
                  <div className="title-and-variant">
                    <div className="product-title line-item-title">
                      <span>Product:</span> {lineItem.productTitle}
                    </div>
                    <div className="variant-title line-item-variant">
                      <span>Variant:</span> {lineItem.variantTitle}
                    </div>
                    <div className="product-stats">
                      <div ><strong>Quantity:</strong> {lineItem.quantity}</div>
                      <div ><strong>Price:</strong> {formatCurrency(lineItem.totalPrice)}</div>
                      
                      {order.printfulTotalFees > 0 && (
                        <div className="stat-item printful-info-item" >
                          <div style={{ fontSize: '12px', color: '#1976d2', fontWeight: 'bold' }}>
                            Printful Costs (Order Level):
                          </div>
                          <div style={{ fontSize: '11px', color: '#555', marginTop: '2px' }}>
                            • Total Order Printful Costs: {formatCurrency(order.printfulTotalFees)}
                            {order.printfulDigitizationFees > 0 && (
                              <span> (includes {formatCurrency(order.printfulDigitizationFees)} digitization)</span>
                            )}
                          </div>
                          {order.printfulDiscount > 0 && (
                            <div style={{ fontSize: '11px', color: '#0c5460', marginTop: '2px' }}>
                              • Sherpa Network Discount Applied: +{formatCurrency(order.printfulDiscount)}
                            </div>
                          )}
                          <div style={{ fontSize: '11px', color: '#666', fontStyle: 'italic', marginTop: '2px' }}>
                            These costs apply to the entire order, not individual items
                          </div>
                        </div>
                      )}

                      {order.yoycolOrderCosts && (
                        <div className="stat-item yoycol-info-item">
                          <div style={{ fontSize: '12px', color: '#7b3fa0', fontWeight: 'bold' }}>
                            Yoycol Costs (Order Level):
                          </div>
                          <div style={{ fontSize: '11px', color: '#555', marginTop: '2px' }}>
                            • Product Base Cost: {formatCurrency(order.yoycolOrderCosts.subtotal)}
                          </div>
                          <div style={{ fontSize: '11px', color: '#555', marginTop: '2px' }}>
                            • Fulfillment Shipping: {formatCurrency(order.yoycolOrderCosts.shipping)}
                          </div>
                          <div style={{ fontSize: '11px', color: '#666', fontStyle: 'italic', marginTop: '2px' }}>
                            These costs apply to the entire order, not individual items
                          </div>
                        </div>
                      )}

                      {lineItem.costSource === 'yoycol_csv' && (
                        <div style={{ fontSize: '11px', color: '#0c5460', marginTop: '4px' }}>
                          • This item's cost: {formatCurrency(lineItem.estimatedCost)} (from Yoycol export)
                        </div>
                      )}
                      
                      {!order.printfulTotalFees && !order.yoycolOrderCosts && (
                        <div className="stat-item no-printful-item" style={{ fontSize: '11px', color: '#999', fontStyle: 'italic', marginTop: '4px' }}>
                          No fulfillment cost data found for this order
                        </div>
                      )}
                    </div>
                  </div>
                </div>
              </div>
            </div>
          );
        }) || <div>No line items available</div>}
      </div>
    </div>
  );
};

export default OrderItemsExpanded;
